import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import '@atlaskit/css-reset/dist/bundle.css'
import { store } from './store/store'
import { NotificationProvider } from './contexts/NotificationContext'
import { LicenseProvider } from './contexts/LicenseContext'
import { AppLayout } from './components/Layout/AppLayout'
import { ReleaseList } from './components/ReleaseList/ReleaseList'
import { FolderBrowser } from './components/FolderBrowser'
import { Library } from './components/Library/Library'
import { Settings } from './components/Settings/Settings'
import App from './App.jsx'
import { IssuePanel } from './components/IssueGlance/IssuePanel'
import { LinkScenariosPage } from './components/IssueGlance/LinkScenariosPage'
import './index.css'
import { isJiraConnect, getNavigationState, replaceNavigationState } from './utils/jiraContext'
import { AwsRum } from 'aws-rum-web'
import RumErrorBoundary from './components/common/RumErrorBoundary'

/**
 * THEME INITIALIZATION - Must happen before first React render
 * This prevents flash of wrong theme (FOUC) when loading in Jira
 *
 * AP.theming.initializeTheming() does the following:
 * - Fetches current theme from Jira parent frame
 * - Sets data-color-mode attribute on <html> element
 * - Injects CSS variables for Atlaskit tokens
 * - Sets up listener for theme changes
 */
if (typeof window !== 'undefined' && window.AP?.theming?.initializeTheming) {
  window.AP.theming.initializeTheming()
}

// CloudWatch RUM - only initializes in UAT/prod builds where env vars are set
const rumAppMonitorId = import.meta.env.VITE_RUM_APP_MONITOR_ID
const rumIdentityPoolId = import.meta.env.VITE_RUM_IDENTITY_POOL_ID
if (rumAppMonitorId && rumIdentityPoolId) {
  try {
    new AwsRum(rumAppMonitorId, '1.0.0', 'us-east-1', {
      sessionSampleRate: 1,
      identityPoolId: rumIdentityPoolId,
      endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: true,
      signing: true
    })
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
}

/**
 * ROUTE SYNCHRONIZER COMPONENT
 *
 * Persists the current route to localStorage and restores it on refresh.
 * This solves the problem where refreshing in Jira iframe loses the route.
 *
 * - On mount: Reads saved route from localStorage and navigates to it
 * - On route change: Saves current route to localStorage
 * - Also syncs with Jira's AP.history for shareable URLs (if available)
 */
function RouteSynchronizer() {
  const location = useLocation()
  const navigate = useNavigate()
  const hasRestoredRef = useRef(false)

  // On mount: Restore route from localStorage (only once)
  useEffect(() => {
    const restoreRoute = async () => {
      if (hasRestoredRef.current) return
      // Skip route restore for standalone panels and dialogs
      if (location.pathname === '/issue-panel' || location.pathname === '/link-scenarios-dialog') return
      hasRestoredRef.current = true

      // First try sessionStorage (per-tab, works for refresh)
      // Fall back to localStorage for cross-iframe handoff (IssuePanel → GeneralPage)
      const savedRoute = sessionStorage.getItem('current-route')
        || localStorage.getItem('current-route')
      // If we read from localStorage handoff, consume it so it doesn't leak to other tabs
      if (!sessionStorage.getItem('current-route') && localStorage.getItem('current-route')) {
        localStorage.removeItem('current-route')
      }
      console.log('[RouteSynchronizer] Saved route from storage:', savedRoute)
      console.log('[RouteSynchronizer] Current location:', location.pathname)

      // Also try AP.history for shared URLs (if available)
      let apRoute = null
      if (isJiraConnect()) {
        try {
          apRoute = await getNavigationState()
          console.log('[RouteSynchronizer] AP route:', apRoute)
        } catch (e) {
          console.warn('[RouteSynchronizer] AP.history.getState not available:', e)
        }
      }

      // Prefer AP route (from shared URL) over localStorage
      const routeToRestore = apRoute || savedRoute
      console.log('[RouteSynchronizer] Route to restore:', routeToRestore)

      // Only navigate if we have a saved route that's different from default
      if (routeToRestore && routeToRestore !== '/releases' && routeToRestore !== '/') {
        console.log('[RouteSynchronizer] Restoring route:', routeToRestore)
        // Small delay to ensure router is ready after initial redirect
        setTimeout(() => {
          console.log('[RouteSynchronizer] Navigating to:', routeToRestore)
          navigate(routeToRestore, { replace: true })
        }, 100)
      } else {
        console.log('[RouteSynchronizer] No route to restore or already on default page')
      }
    }

    restoreRoute()
  }, [])

  // On route change: Save to localStorage and AP.history
  useEffect(() => {
    const fullPath = location.pathname + location.search
    console.log('[RouteSynchronizer] Route changed to:', fullPath)

    // Don't save the root redirect, empty paths, or standalone panel routes
    if (fullPath && fullPath !== '/' && !fullPath.startsWith('/issue-panel') && !fullPath.startsWith('/link-scenarios-dialog')) {
      // Save to sessionStorage (per-tab, reliable for refresh)
      sessionStorage.setItem('current-route', fullPath)
      console.log('[RouteSynchronizer] Saved to sessionStorage:', fullPath)

      // Also save to AP.history for shareable URLs (if available)
      if (isJiraConnect()) {
        try {
          replaceNavigationState(fullPath)
          console.log('[RouteSynchronizer] Saved to AP.history:', fullPath)
        } catch (e) {
          console.warn('[RouteSynchronizer] AP.history.replaceState not available:', e)
        }
      }
    }
  }, [location.pathname, location.search])

  return null
}

/**
 * APPLICATION ROUTING STRUCTURE
 *
 * Uses HashRouter for Jira iframe compatibility.
 * Hash-based routing works correctly in Jira add-on iframes.
 *
 * Routes:
 * - /                    → Redirects to /releases
 * - /releases            → Release list page
 * - /releases/:releaseId → Folder browser for a specific release
 * - /releases/:releaseId/scenario/:scenarioId → Scenario detail page
 * - /library             → Library page (placeholder)
 * - /settings            → Settings page (placeholder)
 */
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Provider store={store}>
      <RumErrorBoundary>
      <NotificationProvider>
        <LicenseProvider>
        <HashRouter>
          <RouteSynchronizer />
          <Routes>
            {/* Main layout with global sidebar */}
            <Route path="/" element={<AppLayout />}>
              {/* Default redirect to releases */}
              <Route index element={<Navigate to="/releases" replace />} />

              {/* Release list page - shows all releases for the project */}
              <Route path="releases" element={<ReleaseList />} />

              {/* Folder browser for a specific release */}
              <Route path="releases/:releaseId" element={<FolderBrowser />} />

              {/* Scenario detail page - existing App.jsx component */}
              {/* This shows test cases, steps, and execution status */}
              <Route path="releases/:releaseId/scenario/:scenarioId" element={<App />} />

              {/* Library page - reusable test scenario templates */}
              <Route path="library" element={<Library />} />

              {/* Library scenario detail page - view/edit library scenario (no test runs) */}
              <Route path="library/scenario/:scenarioId" element={<App isLibrary={true} />} />

              {/* Settings page - placeholder for configuration */}
              <Route path="settings" element={<Settings />} />

              {/* Catch-all for old scenario route pattern (backwards compatibility) */}
              <Route path="scenario/:scenarioId" element={<App />} />
            </Route>

            {/* Jira Issue Panel - standalone route, no AppLayout wrapper */}
            {/* Rendered inside Jira issue page iframe via jiraIssueContents module */}
            <Route path="issue-panel" element={<IssuePanel />} />

            {/* Jira Dialog - Link Scenarios dialog opened via AP.dialog.create() */}
            {/* Renders as full-screen overlay on top of Jira */}
            <Route path="link-scenarios-dialog" element={<LinkScenariosPage />} />

            {/* 404 - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
        </LicenseProvider>
      </NotificationProvider>
      </RumErrorBoundary>
    </Provider>
  </React.StrictMode>,
)
